import React, { useState, useEffect, useRef } from 'react';
import { 
  Activity, BookOpen, EyeOff, ArrowUp, Lock, Unlock, Trash2
} from 'lucide-react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, getDoc, collection, onSnapshot, query, addDoc, deleteDoc, getDocs } from 'firebase/firestore';
import * as THREE from 'three';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const appId = "jarvis-v8-neural";

export default function App() {
  const [booting, setBooting] = useState(true);
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('chat');
  const [input, setInput] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [ghostMode, setGhostMode] = useState(false);
  const [studyMode, setStudyMode] = useState(false);
  const [isUnrestricted, setIsUnrestricted] = useState(false);
  const [pinInput, setPinInput] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [config, setConfig] = useState({
    openRouterKey: "", 
    userName: "Sir",
    mood: "Standard",
  });

  const chatEndRef = useRef(null);
  const orbContainerRef = useRef(null);

  const THEMES = {
    "Standard": { primary: "#00FBFF", orb: 0x00FBFF, label: "J.A.R.V.I.S" },
    "Ruby & Scrim": { primary: "#FF0000", orb: 0x660000, label: "7th Ward Lords" },
    "Limp Bizkit": { primary: "#FFD700", orb: 0xFF0000, label: "FRED DURST" },
    "Serj Tankian": { primary: "#00FF41", orb: 0x00FF41, label: "SYSTEM_CORE" },
    "Eminem": { primary: "#FFFFFF", orb: 0xAAAAAA, label: "SLIM SHADY" },
    "Ye": { primary: "#FFFFFF", orb: 0xFFFFFF, label: "YEEZY" }
  };

  const activeTheme = THEMES[config.mood] || THEMES.Standard;

  // --- THREE.JS ORB ---
  useEffect(() => {
    if (booting || !orbContainerRef.current) return;
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, 1, 0.1, 1000);
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setSize(500, 500);
    orbContainerRef.current.innerHTML = '';
    orbContainerRef.current.appendChild(renderer.domElement);

    const orb = new THREE.Mesh(
      new THREE.IcosahedronGeometry(1.8, 15),
      new THREE.MeshPhongMaterial({ color: activeTheme.orb, wireframe: true, transparent: true, opacity: 0.6, emissive: activeTheme.orb, emissiveIntensity: 0.3 })
    );
    scene.add(orb);
    scene.add(new THREE.PointLight(activeTheme.orb, 1.5, 100));
    camera.position.z = 4.5;

    let frameId;
    const animate = () => {
      frameId = requestAnimationFrame(animate);
      orb.rotation.y += 0.005;
      const scale = 1 + Math.sin(Date.now() * (isGenerating ? 0.01 : 0.002)) * 0.05;
      orb.scale.set(scale, scale, scale);
      renderer.render(scene, camera);
    };
    animate();
    return () => { cancelAnimationFrame(frameId); renderer.dispose(); };
  }, [booting, activeTheme, isGenerating]);

  // --- FIREBASE AUTH ---
  useEffect(() => {
    signInAnonymously(auth).catch(console.error);
    return onAuthStateChanged(auth, setUser);
  }, []);

  // --- FIREBASE DATA ---
  useEffect(() => {
    if (!user) return;
    const configRef = doc(db, 'artifacts', appId, 'users', user.uid, 'settings', 'core');
    getDoc(configRef).then(s => { if (s.exists()) setConfig(v => ({ ...v, ...s.data() })); });
    
    const q = query(collection(db, 'artifacts', appId, 'users', user.uid, 'history'));
    return onSnapshot(q, (snap) => {
      setChatHistory(snap.docs.map(d => ({...d.data(), id: d.id})).sort((a, b) => a.timeIndex - b.timeIndex));
    });
  }, [user]);

  // --- ACTIONS ---
  const handlePurge = async () => {
    if (!user) return;
    const s = await getDocs(query(collection(db, 'artifacts', appId, 'users', user.uid, 'history')));
    s.forEach(d => deleteDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'history', d.id)));
  };

  const handleSend = async (e) => {
    if (e) e.preventDefault();
    if (!input.trim() || isGenerating || !user) return;
    const msg = input;
    setInput("");
    if (!ghostMode) {
      await addDoc(collection(db, 'artifacts', appId, 'users', user.uid, 'history'), { role: 'user', text: msg, timeIndex: Date.now() });
    }
    setIsGenerating(true);
    try {
      const response = await fetch("https://openrouter.ai/api/v1/chat/completions", {
        method: "POST",
        headers: { "Authorization": `Bearer ${config.openRouterKey}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          model: studyMode ? "google/gemini-2.0-flash-thinking-exp:free" : "google/gemini-2.0-flash-001",
          messages: [{ role: "system", content: `You are ${activeTheme.label}. User: ${config.userName}.` }, { role: "user", content: msg }]
        })
      });
      const data = await response.json();
      const aiText = data?.choices?.[0]?.message?.content || "ERROR";
      if (!ghostMode) {
        await addDoc(collection(db, 'artifacts', appId, 'users', user.uid, 'history'), { role: 'model', text: aiText, persona: activeTheme.label, timeIndex: Date.now() });
      } else {
        setChatHistory(p => [...p, { role: 'user', text: msg }, { role: 'model', text: aiText, persona: 'GHOST' }]);
      }
    } catch (err) { console.error(err); }
    setIsGenerating(false);
  };

  useEffect(() => { setTimeout(() => setBooting(false), 2000); }, []);
  useEffect(() => chatEndRef.current?.scrollIntoView({ behavior: "smooth" }), [chatHistory]);

  if (booting) return <div className="h-screen bg-black flex items-center justify-center text-[#00FBFF] font-mono animate-pulse uppercase tracking-widest">Booting_v8</div>;

  return (
    <div className={`h-screen bg-black text-white flex flex-col overflow-hidden relative ${studyMode ? 'grayscale' : ''}`}>
      <div className="absolute inset-0 flex items-center justify-center opacity-20 pointer-events-none"><div ref={orbContainerRef}></div></div>
      
      <header className="h-20 border-b border-white/5 flex items-center justify-between px-8 z-20 backdrop-blur-md">
        <div className="font-black text-2xl italic" style={{ color: activeTheme.primary }}>{activeTheme.label}</div>
        <nav className="flex gap-4">
          <button onClick={() => setActiveTab('chat')} className={`px-6 py-2 rounded-full text-[10px] font-black uppercase ${activeTab === 'chat' ? 'bg-white text-black' : 'opacity-40'}`}>Chat</button>
          <button onClick={() => setActiveTab('settings')} className={`px-6 py-2 rounded-full text-[10px] font-black uppercase ${activeTab === 'settings' ? 'bg-white text-black' : 'opacity-40'}`}>Settings</button>
        </nav>
      </header>

      <main className="flex-1 flex overflow-hidden z-10">
        <div className="flex-1 flex flex-col overflow-hidden">
          {activeTab === 'chat' ? (
            <>
              <div className="flex-1 overflow-y-auto p-8 space-y-8">
                {chatHistory.map((m, i) => (
                  <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className="max-w-[80%] bg-white/5 border border-white/10 rounded-2xl p-6">
                      <div className="text-[9px] font-black mb-2 uppercase" style={m.role !== 'user' ? { color: activeTheme.primary } : {}}>{m.role === 'user' ? config.userName : m.persona}</div>
                      <div className="text-lg">{m.text}</div>
                    </div>
                  </div>
                ))}
                <div ref={chatEndRef} />
              </div>
              <form onSubmit={handleSend} className="p-8 bg-black">
                <div className="bg-white/5 border border-white/10 rounded-full flex items-center p-2">
                  <input value={input} onChange={e => setInput(e.target.value)} placeholder="System ready..." className="flex-1 bg-transparent px-6 py-4 outline-none" />
                  <button type="submit" className="p-4 bg-white text-black rounded-full"><ArrowUp size={20}/></button>
                </div>
              </form>
            </>
          ) : (
            <div className="p-12 space-y-8 overflow-y-auto w-full max-w-2xl">
              <div className="grid grid-cols-2 gap-4">
                {Object.keys(THEMES).map(p => (
                  <button key={p} onClick={() => setConfig({...config, mood: p})} className={`p-4 border rounded-xl text-[10px] font-black uppercase ${config.mood === p ? 'bg-white text-black' : 'border-white/10'}`}>{p}</button>
                ))}
              </div>
              <input type="password" value={config.openRouterKey} onChange={e => setConfig({...config, openRouterKey: e.target.value})} className="w-full bg-white/5 border border-white/10 p-4 rounded-xl" placeholder="OPENROUTER_API_KEY" />
              <div className="flex gap-2">
                <input type="password" value={pinInput} onChange={e => setPinInput(e.target.value)} className="w-24 bg-white/5 border border-white/10 p-4 rounded-xl" placeholder="PIN" />
                <button onClick={() => { if(pinInput === "4439") setIsUnrestricted(!isUnrestricted); }} className="p-4 border border-white/10 rounded-xl">{isUnrestricted ? <Unlock size={16}/> : <Lock size={16}/>}</button>
                <button onClick={handlePurge} className="flex-1 p-4 border border-red-500/20 text-red-500 rounded-xl text-[10px] font-black uppercase"><Trash2 size={14} className="inline mr-2"/> Purge History</button>
              </div>
            </div>
          )}
        </div>

        <aside className="w-64 border-l border-white/5 p-6 space-y-6 hidden md:flex flex-col">
          <button onClick={() => setStudyMode(!studyMode)} className={`w-full flex justify-between p-4 rounded-xl border ${studyMode ? 'bg-white text-black' : 'border-white/5 opacity-50'}`}><span className="text-[9px] font-black uppercase">Study</span><BookOpen size={14}/></button>
          <button onClick={() => setGhostMode(!ghostMode)} className={`w-full flex justify-between p-4 rounded-xl border ${ghostMode ? 'bg-red-600 text-white' : 'border-white/5 opacity-50'}`}><span className="text-[9px] font-black uppercase">Ghost</span><EyeOff size={14}/></button>
          <div className="mt-auto flex items-center gap-2 opacity-40"><Activity size={12} className="text-[#00FBFF] animate-pulse"/><span className="text-[8px] font-black uppercase">Core: Online</span></div>
        </aside>
      </main>
    </div>
  );
}
